import { useState, useEffect, useRef } from 'react'
import { ArrowLeft, Users, Trash2, X } from 'lucide-react'
import { useMatch, useCreateMatchTeam, useUpdateMatchTeam, useDeleteMatchTeam, useAddMatchTeamPlayer, useRemoveMatchTeamPlayer } from '../hooks/useMatches'
import { hexToRgba, getInitials } from '../utils/colors'
import Spinner from './Spinner'

export default function TeamSetupPage({ matchId, matchType, isEditing, onBack, onContinueToBuyIn }) {
  const { data: match, isLoading } = useMatch(matchId)
  const createTeam = useCreateMatchTeam()
  const updateTeam = useUpdateMatchTeam()
  const deleteTeam = useDeleteMatchTeam()
  const addPlayer = useAddMatchTeamPlayer()
  const removePlayer = useRemoveMatchTeamPlayer()
  const [addingToTeam, setAddingToTeam] = useState(null)
  const [pressedAddId, setPressedAddId] = useState(null)
  const defaultTeamsCreated = useRef(false)

  // Create default teams (A & B) if none exist yet — runs once via ref guard
  useEffect(() => {
    if (!match) return
    const matchTeams = match.match_teams || []
    if (matchTeams.length === 0 && !defaultTeamsCreated.current) {
      defaultTeamsCreated.current = true
      createTeam.mutate({ match_id: matchId, team_name: 'Team A', team_order: 0 })
      createTeam.mutate({ match_id: matchId, team_name: 'Team B', team_order: 1 })
    }
  }, [match, matchId, createTeam])

  if (isLoading || !match) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spinner message="Loading..." />
      </div>
    )
  }

  const matchPlayers = match.match_players || []
  const matchTeams = (match.match_teams || []).sort((a, b) => a.team_order - b.team_order)

  // Build set of assigned player IDs from match_teams
  const assignedPlayerIds = new Set()
  matchTeams.forEach((team) => {
    (team.match_team_players || []).forEach((mtp) => {
      assignedPlayerIds.add(mtp.player_id)
    })
  })

  const unassignedPlayers = matchPlayers.filter((mp) => !assignedPlayerIds.has(mp.player_id))

  // Build lookup from player_id -> match-specific handicap_index
  const matchHandicapByPlayerId = {}
  matchPlayers.forEach((mp) => {
    matchHandicapByPlayerId[mp.player_id] = mp.handicap_index
  })
  const allAssigned = unassignedPlayers.length === 0 && matchTeams.length >= 2 && matchTeams.every((t) => (t.match_team_players || []).length > 0)
  const canContinue = allAssigned

  const handleAddTeam = () => {
    const nextOrder = matchTeams.length
    const nextLetter = String.fromCharCode(65 + nextOrder)
    createTeam.mutate({ match_id: matchId, team_name: `Team ${nextLetter}`, team_order: nextOrder })
  }

  const handleDeleteTeam = (teamId) => {
    deleteTeam.mutate({ id: teamId, match_id: matchId })
    if (addingToTeam === teamId) setAddingToTeam(null)
  }

  const handleRenameTeam = (teamId, newName) => {
    updateTeam.mutate({ id: teamId, match_id: matchId, team_name: newName })
  }

  const handleAddPlayer = (teamId, playerId) => {
    addPlayer.mutate({ match_team_id: teamId, player_id: playerId, match_id: matchId })
  }

  const handleRemovePlayer = (mtpId) => {
    removePlayer.mutate({ id: mtpId, match_id: matchId })
  }

  // Show spinner while default teams are being created
  if (matchTeams.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spinner message="Setting up teams..." />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={onBack}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-tertiary)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Set Up Teams</h1>
      </div>

      {/* Content */}
      <div style={{ paddingBottom: '6rem' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', lineHeight: 1.5, marginBottom: '1rem' }}>
          Divide your group into teams for the entire event. These rosters will carry through every session, so choose wisely.
        </p>

        {/* Unassigned players banner */}
        {unassignedPlayers.length > 0 && (
          <div style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            border: '1px dashed var(--border-color)',
            textAlign: 'center',
          }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)' }}>
              {unassignedPlayers.length} player{unassignedPlayers.length !== 1 ? 's' : ''} unassigned
            </span>
          </div>
        )}

        {/* Team cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {matchTeams.map((team) => {
            const teamPlayers = team.match_team_players || []
            const isAdding = addingToTeam === team.id

            return (
              <div
                key={team.id}
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                }}
              >
                {/* Team header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    defaultValue={team.team_name}
                    onBlur={(e) => {
                      const val = e.target.value.trim()
                      if (val && val !== team.team_name) handleRenameTeam(team.id, val)
                    }}
                    style={{
                      fontWeight: '700',
                      fontSize: '0.875rem',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: 'var(--text-primary)',
                      padding: '0.25rem 0',
                      width: '120px',
                    }}
                  />
                  {matchTeams.length > 2 && (
                    <button
                      onClick={() => handleDeleteTeam(team.id)}
                      style={{ color: 'var(--text-muted)', padding: '0.25rem' }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Players on this team */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.5rem' }}>
                  {teamPlayers.map((mtp) => {
                    const player = mtp.player
                    if (!player) return null
                    return (
                      <div
                        key={mtp.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.375rem 0.5rem',
                          backgroundColor: 'var(--bg-tertiary)',
                          borderRadius: '0.375rem',
                        }}
                      >
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: player.avatar_color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '0.625rem',
                          color: '#fff',
                          flexShrink: 0,
                        }}>
                          {getInitials(player)}
                        </div>
                        <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: '500' }}>
                          {player.display_name}
                          {matchHandicapByPlayerId[mtp.player_id] != null && (
                            <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}> ({matchHandicapByPlayerId[mtp.player_id]})</span>
                          )}
                        </span>
                        {isAdding && (
                          <button
                            onClick={() => handleRemovePlayer(mtp.id)}
                            style={{ color: 'var(--text-muted)', padding: '0.25rem' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    )
                  })}
                  {teamPlayers.length === 0 && (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', padding: '0.5rem 0' }}>
                      No players yet
                    </p>
                  )}
                </div>

                {/* Add/Edit Players button */}
                {!isAdding && (unassignedPlayers.length > 0 || teamPlayers.length > 0) && (
                  <button
                    onClick={() => setAddingToTeam(team.id)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.375rem',
                      padding: '0.625rem',
                      borderRadius: '9999px',
                      backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                      border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.25)',
                      color: 'var(--accent-primary)',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                    }}
                  >
                    <Users size={16} />
                    Add / Edit Players
                  </button>
                )}

                {/* Expanded player selection */}
                {isAdding && (
                  <div style={{
                    borderRadius: '0.5rem',
                    border: '1px solid var(--accent-primary)',
                    overflow: 'hidden',
                  }}>
                    {unassignedPlayers.length > 0 && (
                      <>
                        <div style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                          borderBottom: '1px solid var(--border-color)',
                        }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                            Select players to add
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {unassignedPlayers.map((mp) => {
                            const player = mp.player
                            if (!player) return null
                            return (
                              <button
                                key={mp.player_id}
                                onClick={() => handleAddPlayer(team.id, mp.player_id)}
                                onTouchStart={() => setPressedAddId(mp.player_id)}
                                onTouchEnd={() => setPressedAddId(null)}
                                onTouchCancel={() => setPressedAddId(null)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.625rem',
                                  padding: '0.75rem',
                                  backgroundColor: 'var(--bg-tertiary)',
                                  borderBottom: '1px solid var(--border-color)',
                                  textAlign: 'left',
                                  transform: pressedAddId === mp.player_id ? 'scale(0.97)' : 'scale(1)',
                                  opacity: pressedAddId === mp.player_id ? 0.7 : 1,
                                  transition: 'transform 0.15s ease, opacity 0.15s ease',
                                }}
                              >
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  backgroundColor: player.avatar_color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '700',
                                  fontSize: '0.75rem',
                                  color: '#fff',
                                  flexShrink: 0,
                                }}>
                                  {getInitials(player)}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                    {player.display_name}
                                    {matchHandicapByPlayerId[mp.player_id] != null && (
                                      <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}> ({matchHandicapByPlayerId[mp.player_id]})</span>
                                    )}
                                  </div>
                                </div>
                                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                                  Add
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      </>
                    )}
                    <button
                      onClick={() => setAddingToTeam(null)}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                        color: 'var(--accent-primary)',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        borderTop: unassignedPlayers.length === 0 ? 'none' : undefined,
                      }}
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Add Team button */}
        <button
          onClick={handleAddTeam}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            padding: '0.75rem',
            borderRadius: '9999px',
            border: '1px dashed var(--border-color)',
            color: 'var(--text-muted)',
            fontSize: '0.8125rem',
            fontWeight: '500',
          }}
        >
          + Add Team
        </button>

      </div>

      {/* Fixed bottom bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '0.75rem 1rem',
        paddingBottom: 'max(1rem, calc(0.25rem + var(--bottom-safe-area, 0px)))',
        backgroundColor: 'var(--bg-primary)',
        zIndex: 101,
      }}>
        {(matchType === 'team_match' || matchType === 'invitational') && !canContinue && (
          <p style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#ef4444',
            marginBottom: '0.375rem',
          }}>
            Assign all players to teams to continue
          </p>
        )}
        <button
          onClick={
            (matchType === 'team_match' || matchType === 'invitational') && onContinueToBuyIn && !isEditing
              ? onContinueToBuyIn
              : onBack
          }
          disabled={(matchType === 'team_match' || matchType === 'invitational') && !canContinue}
          className="btn-primary"
          style={{
            width: '100%',
            height: '48px',
            fontSize: '1rem',
            fontWeight: '600',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: ((matchType === 'team_match' || matchType === 'invitational') && !canContinue) ? 0.5 : 1,
          }}
        >
          {(matchType === 'team_match' || matchType === 'invitational') && !isEditing
            ? 'Continue to Buy-In'
            : 'Done'}
        </button>
      </div>
    </div>
  )
}
